import {type ColorHueKey} from '@sanity/color'
import {EditIcon, ErrorOutlineIcon, InfoOutlineIcon} from '@sanity/icons'
import {Box, Card, Flex, Grid, MenuDivider, Stack, Text} from '@sanity/ui'
import React from 'react'

import {useMatchesMedia} from '@/utils/views'

import {ProgressCircle} from '../../generic/progress-circle/ProgressCircle'
import {Tooltip} from '../../primitives/tooltip'

export type BudgetSpendingOverviewProps = {
  featureName: string
  cap: number | null
  defaultCap: number
  currentSpend: number
  canEditCap?: boolean
  onEditCap: () => void
}

export const BudgetSpendingOverview: React.FC<BudgetSpendingOverviewProps> = ({
  featureName,
  cap,
  defaultCap,
  currentSpend,
  canEditCap,
  onEditCap,
}) => {
  const isMobile = useMatchesMedia(0)

  const noCap = cap === null

  const budgetRemainder = noCap ? currentSpend : Math.max(cap - currentSpend, 0)

  const percentRemainder = noCap ? null : (budgetRemainder / cap) * 100

  const showReachingLimitWarning = percentRemainder ? percentRemainder <= 10 : false

  const showLimitReachedWarning = !noCap && budgetRemainder <= 0

  const progressColor: ColorHueKey = percentRemainder && percentRemainder > 25 ? 'green' : 'red'

  let capText = `$ ${cap}`

  if (noCap) {
    capText = 'Unlimited'
  }
  if (cap === defaultCap && !noCap) {
    capText = `Default ($${defaultCap})`
  }

  return (
    <Stack space={4}>
      <Grid columns={[1, 2]} gapY={4} gapX={6}>
        <Box style={{order: 1}}>
          <Text size={1} muted>
            {noCap ? 'Used this month' : 'Left this month'}
          </Text>
        </Box>
        <Box style={{order: isMobile ? 3 : 2}}>
          <Text size={1} muted>
            Spending cap $
          </Text>
        </Box>
        <Box display={'flex'} style={{order: isMobile ? 2 : 3}}>
          <Flex align={'center'} gap={4}>
            {percentRemainder ? (
              <ProgressCircle
                color={progressColor}
                direction="counter-clockwise"
                percentage={percentRemainder}
                size={36}
              />
            ) : null}
            <Text size={4}>${budgetRemainder}</Text>
          </Flex>
        </Box>
        <Box style={{order: 4}}>
          <Tooltip
            disabled={canEditCap}
            portal
            content={"You don't have permission to edit the spending cap"}
          >
            <Card
              as={canEditCap ? 'button' : undefined}
              onClick={canEditCap ? onEditCap : undefined}
              radius={2}
            >
              <Stack>
                <MenuDivider />
                <Flex
                  gap={4}
                  paddingLeft={2}
                  paddingRight={4}
                  paddingY={4}
                  justify={'space-between'}
                >
                  <Text muted={!canEditCap}>{capText}</Text>
                  <Text muted={!canEditCap}>{canEditCap ? <EditIcon /> : <InfoOutlineIcon />}</Text>
                </Flex>
                <MenuDivider />
              </Stack>
            </Card>
          </Tooltip>
        </Box>
      </Grid>
      {showReachingLimitWarning && (
        <Card tone={'critical'} padding={4} radius={3}>
          <Flex align={'center'} gap={3}>
            <Text size={1}>
              <ErrorOutlineIcon />
            </Text>
            <Text size={1}>
              You are about to reach your spending cap on {featureName.toLowerCase()}
            </Text>
          </Flex>
        </Card>
      )}
      {showLimitReachedWarning && (
        <Card tone={'caution'} padding={4} radius={3}>
          <Flex align={'center'} gap={3}>
            <Text size={1}>
              <ErrorOutlineIcon />
            </Text>
            <Text size={1}>
              You've reached your spending cap on {featureName.toLowerCase()} this month.
            </Text>
          </Flex>
        </Card>
      )}
    </Stack>
  )
}
